'use client'

/**
 * Thin client-side wrappers around the Datadog Browser RUM SDK.
 *
 * Init lives in `instrumentation-client.ts` (Next.js loads it before
 * React mounts); the Clerk→RUM identity bridge is
 * `@brew/observability/datadog-user-sync`, mounted in `app/layout.tsx`. These helpers can call the SDK synchronously and trust
 * that init has either happened or is a deliberate no-op (env vars
 * missing / `NODE_ENV !== 'production'`). The Datadog SDK's own APIs
 * are no-ops before init, so calling these in dev never throws.
 *
 * Every helper is fire-and-forget — never `await` from a hot path.
 */

import {
  addRumAction,
  addRumError,
  addRumNextjsError,
  sanitizeRumEvent,
} from '@brew/observability/browser'

export type RumNoiseFilterEvent = {
  type: string
  error?: {
    message?: unknown
  }
}

/** Return true when a known benign browser or extension error should be dropped. */
export function shouldDropRumEvent(event: RumNoiseFilterEvent): boolean {
  return !sanitizeRumEvent(event)
}

/** Submit a custom RUM action (product-analytics-style event). */
export function submitDDEvent(
  event: string,
  context?: Record<string, unknown>
): void {
  addRumAction(event, context)
}

/** Report a recoverable client error with feature-specific RUM context. */
export function reportBrowserError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  addRumError(error, context)
}

/**
 * Forward a thrown error from a Next.js `error.tsx` / `global-error.tsx`
 * boundary into RUM with the Next.js-specific framing
 * (`error.digest`, server vs. client component, etc.).
 */
export function reportNextjsError(error: Error & { digest?: string }): void {
  addRumNextjsError(error)
}
